import {
	IonButton,
	IonButtons,
	IonCol,
	IonContent,
	IonGrid,
	IonHeader,
	IonIcon,
	IonInput,
	IonItem,
	IonLabel,
	IonPage,
	IonRow,
	IonText,
	IonTitle,
	IonToast,
	IonToolbar,
} from "@ionic/react";
import { useHistory } from "react-router";
import { arrowBackOutline } from "ionicons/icons";
// import * as styles from './ManagementPage.css';
import "./ManagementPage.css";
import {
	getProcessStatus,
	getRetrievalAccuracy,
	resetPerformance,
	RetrievalAccuracy,
	triggerIndexImages,
} from "../../api";
import { useCallback, useEffect, useState } from "react";
import { isEmpty, toLower } from "lodash";
import useAuth from "../../context/auth.context";
import { catchError, EMPTY, switchMap } from "rxjs";
import { useTranslation } from "react-i18next";
import { DEFAULT_TOAST_DURATION } from '../../constants';

export function ManagementPage() {
	const history = useHistory();
	const [retrievalStatistics, setRetrievalStatistics] =
		useState<RetrievalAccuracy>();
	const [activationPhrase, setActivationPhrase] = useState<string>();
	const [resetConfirmed, setResetConfirmed] = useState<boolean>(false);
	const [isIdleProcess, setIsIdleProcess] = useState<boolean>(false);
	const [triggeredIndex, setTriggeredIndex] = useState<boolean>(false);
	const [toastMessage, setToastMessage] = useState<string>("");

	const { acquireToken } = useAuth();
	const { t } = useTranslation();

	const fetchData = useCallback(async () => {
		acquireToken()
			.pipe(
				switchMap((accessToken) => {
					return getRetrievalAccuracy(accessToken);
				}),
				catchError((error) => {
					console.error(error);
					setToastMessage(t("management.fetchRetrievalAccuracyError"));
					return EMPTY;
				})
			)
			.subscribe((result) => {
				setRetrievalStatistics(result);
			});
	}, []);

	useEffect(() => {
		fetchData();
		fetchProcessStatus();
	}, []);

	function onResetPerformance() {
		acquireToken()
			.pipe(
				switchMap((accessToken) => {
					return resetPerformance(accessToken);
				}),
				catchError((error) => {
					console.error(error);
					setToastMessage(t("management.resetPerformanceError"));
					return EMPTY;
				})
			)
			.subscribe(setResetConfirmed);
	}

	function onBack() {
		history.push("/landing");
	}

	function onActivationChange(event: any) {
		const text = event.target.value;
		setActivationPhrase(text);
	}

	function isActivated() {
		return toLower(activationPhrase) === "reset";
	}

	function fetchProcessStatus() {
		acquireToken()
			.pipe(
				switchMap((accessToken) => {
					return getProcessStatus(accessToken);
				}),
				catchError((error) => {
					console.error(error);
					setToastMessage(t("management.fetchProcessStatusError"));
					return EMPTY;
				})
			)
			.subscribe((result) => {
				setIsIdleProcess(result);
			});
	}

	function onIndexImages() {
		acquireToken()
			.pipe(
				switchMap((accessToken) => {
					return triggerIndexImages(accessToken);
				}),
				catchError((error) => {
					console.error(error);
					setToastMessage(t("management.triggerImageIndexingError"));
					return EMPTY;
				})
			)
			.subscribe((result) => {
				setTriggeredIndex(result);
				if (result) {
					setIsIdleProcess(false);
				}
			});
	}

	return (
		<IonPage>
			<IonHeader>
				<IonToolbar>
					<IonButtons slot="start">
						<IonButton onClick={onBack} size="large">
							<IonIcon icon={arrowBackOutline} slot="icon-only" />
						</IonButton>
					</IonButtons>
					<IonTitle>{t("management.title")}</IonTitle>
				</IonToolbar>
			</IonHeader>
			<IonContent className="ion-padding">
				<IonGrid>
					<IonRow className="row">
						<h4>{t("management.tableTitle")}</h4>
					</IonRow>
					<IonRow>
						<IonCol>{t("management.top1Column")}</IonCol>
						<IonCol>{t("management.top5Column")}</IonCol>
						<IonCol>{t("management.top10Column")}</IonCol>
						<IonCol>{t("management.missing")}</IonCol>
					</IonRow>
					<IonRow>
						<IonCol>{retrievalStatistics?.top1_accuracy}</IonCol>
						<IonCol>{retrievalStatistics?.top5_accuracy}</IonCol>
						<IonCol>{retrievalStatistics?.top10_accuracy}</IonCol>
						<IonCol>{retrievalStatistics?.missing}</IonCol>
					</IonRow>
				</IonGrid>
				<IonGrid>
					<IonRow>
						<IonCol>{t("management.top1Count")}</IonCol>
						<IonCol>{t("management.top5Count")}</IonCol>
						<IonCol>{t("management.top10Count")}</IonCol>
						<IonCol>{t("management.missingCount")}</IonCol>
					</IonRow>
					<IonRow>
						<IonCol>{retrievalStatistics?.top1_count}</IonCol>
						<IonCol>{retrievalStatistics?.top5_count}</IonCol>
						<IonCol>{retrievalStatistics?.top10_count}</IonCol>
						<IonCol>{retrievalStatistics?.missed_count}</IonCol>
					</IonRow>
				</IonGrid>
				<IonItem
					style={{
						marginTop: "2rem",
					}}
				>
					<IonLabel position="stacked">
						{t("management.passwordInputLabel")}
					</IonLabel>
					<IonInput
						onIonChange={onActivationChange}
						placeholder="Password"
						type="password"
					/>
				</IonItem>
				<IonButton
					disabled={!isActivated()}
					expand="block"
					onClick={onResetPerformance}
				>
					{t("general.reset")}
				</IonButton>
				{resetConfirmed && (
					<IonItem fill="outline">
						<IonText color="success">
							{t("management.successfulReset")}
						</IonText>
					</IonItem>
				)}
				<IonButton
					disabled={!isIdleProcess || !isActivated()}
					expand="block"
					onClick={onIndexImages}
					style={{
						marginTop: "2rem",
					}}
				>
					{t("management.triggerImageIndexingButton")}
				</IonButton>
				{triggeredIndex && (
					<IonItem fill="outline">
						<IonText color="success">
							{t("management.triggerImageIndexingTriggered")}
						</IonText>
					</IonItem>
				)}
			</IonContent>
			<IonToast
				duration={DEFAULT_TOAST_DURATION}
				isOpen={!isEmpty(toastMessage)}
				onDidDismiss={() => setToastMessage("")}
				message={toastMessage}
			/>
		</IonPage>
	);
}
